import React, { useState, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC, s_GB } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';

function playSpatialTone(pan, freq = 440, duration = 0.5) {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine'; osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.4, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + duration);
    osc.connect(gain);
    if (ac.createStereoPanner) {
      const panner = ac.createStereoPanner();
      panner.pan.value = pan;
      gain.connect(panner); panner.connect(ac.destination);
    } else {
      gain.connect(ac.destination);
    }
    osc.start(); osc.stop(ac.currentTime + duration);
    setTimeout(() => ac.close(), (duration + 0.2) * 1000);
  } catch {}
}

const POSITIONS = [
  { id: 'left',   pan: -0.9, icon: '⬅', labelNL: 'Links',  labelEN: 'Left'   },
  { id: 'center', pan:  0,   icon: '⬆', labelNL: 'Midden', labelEN: 'Center' },
  { id: 'right',  pan:  0.9, icon: '➡', labelNL: 'Rechts', labelEN: 'Right'  },
];
const ROUNDS = 12;

function SoundLocateGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [phase, setPhase] = useState('start'); // start | playing | answer | result | done
  const [target, setTarget] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const timerRef = useRef(null);

  const startRound = () => {
    const pos = POSITIONS[Math.floor(Math.random() * POSITIONS.length)];
    setTarget(pos); setFeedback(null); setPhase('playing');
    const freq = 300 + Math.random() * 300;
    playSpatialTone(pos.pan, freq, 0.7);
    timerRef.current = setTimeout(() => setPhase('answer'), 900);
  };

  const answer = (posId) => {
    if (phase !== 'answer') return;
    const isRight = posId === target.id;
    setFeedback(isRight ? 'correct' : 'wrong');
    setPhase('result');
    if (isRight) { setScore(s => s + 1); playSpatialTone(0, 880, 0.15); }
    else { playSpatialTone(0, 200, 0.3); }
    timerRef.current = setTimeout(() => {
      const next = round + 1;
      if (next >= ROUNDS) { setPhase('done'); saveGameScore('soundlocate', Math.round((score + (isRight ? 1 : 0)) / ROUNDS * 100)); }
      else { setRound(next); startRound(); }
    }, 900);
  };

  if (phase === 'done') {
    return (
      <div>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>← {nl ? 'Terug' : 'Back'}</button>
        <div style={{ ...s_GC({ padding: 24, textAlign: 'center' }) }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>👂</div>
          <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{nl ? 'Klaar!' : 'Done!'}</div>
          <div style={{ color: _T.accent, fontSize: 32, fontWeight: 700 }}>{score}/{ROUNDS}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 16 }}>{nl ? 'correcte antwoorden' : 'correct answers'}</div>
          <button style={s_PB({ width: '100%' })} onClick={() => { setRound(0); setScore(0); setPhase('start'); }}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>← {nl ? 'Terug' : 'Back'}</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ color: C.silver, fontSize: 12 }}>{nl ? `Ronde ${round + 1}/${ROUNDS}` : `Round ${round + 1}/${ROUNDS}`}</span>
        <span style={{ color: _T.accent, fontSize: 12, fontWeight: 700 }}>{score} ✓</span>
      </div>
      <div style={{ fontSize: 11, color: C.silver, marginBottom: 8, textAlign: 'center' }}>{nl ? '🎧 Gebruik je koptelefoon voor het beste resultaat' : '🎧 Use headphones for best results'}</div>

      <div style={{ ...s_GC({ padding: 28, textAlign: 'center', marginBottom: 14 }) }}>
        {phase === 'start' && <><div style={{ fontSize: 48 }}>👂</div><div style={{ color: C.silver, fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{nl ? 'Hoor je het geluid links, midden of rechts?' : 'Do you hear the sound on the left, center or right?'}</div></>}
        {phase === 'playing' && <><div style={{ fontSize: 48 }} className="popIn">🔊</div><div style={{ color: C.orange, fontWeight: 700, marginTop: 8 }}>{nl ? 'Luister...' : 'Listening...'}</div></>}
        {phase === 'answer' && <><div style={{ fontSize: 48 }}>🤔</div><div style={{ color: C.white, fontWeight: 700, marginTop: 8 }}>{nl ? 'Waar hoorde je het?' : 'Where did you hear it?'}</div></>}
        {phase === 'result' && <><div style={{ fontSize: 48 }}>{feedback === 'correct' ? '✅' : '❌'}</div><div style={{ color: feedback === 'correct' ? C.teal : C.red, fontWeight: 700, marginTop: 8 }}>{feedback === 'correct' ? (nl ? 'Goed!' : 'Correct!') : (nl ? 'Fout!' : 'Wrong!')}</div><div style={{ color: C.silver, fontSize: 11, marginTop: 4 }}>{nl ? target?.labelNL : target?.labelEN}</div></>}
      </div>

      {phase === 'start' && <button style={s_PB({ width: '100%' })} onClick={startRound}>{nl ? 'Start' : 'Start'}</button>}
      {phase === 'answer' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {POSITIONS.map(pos => (
            <button key={pos.id} style={s_GB({ padding: '16px 0', fontSize: 20, width: '100%' })} onClick={() => answer(pos.id)}>
              {pos.icon}<div style={{ fontSize: 11, marginTop: 4 }}>{nl ? pos.labelNL : pos.labelEN}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SoundLocateGame;
